import React, { useState, useRef } from 'react';
import { 
  AppBar, 
  Toolbar, 
  Typography, 
  Button, 
  Box, 
  Menu, 
  MenuItem, 
  Dialog, 
  DialogTitle, 
  DialogContent, 
  DialogContentText, 
  DialogActions,
  Snackbar,
  Alert
} from '@mui/material';
import SettingsIcon from '@mui/icons-material/Settings';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import CloudDownloadIcon from '@mui/icons-material/CloudDownload';
import DeleteForeverIcon from '@mui/icons-material/DeleteForever';
import HomeIcon from '@mui/icons-material/Home';
import { exportDataToFile, importDataFromFile } from '../../utils/importExport';
import { useData } from '../../context/DataContext';
import { useNavigate } from 'react-router-dom';

const EnHeader = ({ onSearch, onLanguageChange }) => {
  const { refreshData, clearAllData } = useData();
  const fileInputRef = useRef(null);
  const navigate = useNavigate();
  
  // Menu state
  const [anchorEl, setAnchorEl] = useState(null);
  const [settingsAnchorEl, setSettingsAnchorEl] = useState(null);
  const open = Boolean(anchorEl);
  const settingsOpen = Boolean(settingsAnchorEl);
  
  // Dialog state
  const [importDialogOpen, setImportDialogOpen] = useState(false);
  const [exportDialogOpen, setExportDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  
  // Snackbar state
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'info'
  });

  // Open data management menu
  const handleDataMenuClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  // Close data management menu
  const handleDataMenuClose = () => {
    setAnchorEl(null);
  };

  // Open import dialog
  const handleImportClick = () => {
    handleDataMenuClose();
    setImportDialogOpen(true);
  };

  // Open export dialog
  const handleExportClick = () => {
    handleDataMenuClose();
    setExportDialogOpen(true);
  };

  // Close import dialog
  const handleImportDialogClose = () => {
    setImportDialogOpen(false);
  };

  // Close export dialog
  const handleExportDialogClose = () => {
    setExportDialogOpen(false);
  };

  // Open file selection dialog
  const handleFileSelect = () => {
    fileInputRef.current.click();
  };

  // Handle file selection
  const handleFileChange = async (event) => {
    const files = event.target.files;
    if (!files || files.length === 0) return;
    
    // Show processing snackbar
    setSnackbar({
      open: true,
      message: `Importing ${files.length} file(s)...`,
      severity: 'info'
    });
    
    let successCount = 0;
    let errorCount = 0;
    
    // Process each file
    for (let i = 0; i < files.length; i++) {
      try {
        console.log('Import started:', files[i].name);
        const result = await importDataFromFile(files[i], { overwriteExisting: false });
        console.log('Import result:', result);
        if (result.success) {
          successCount++;
        } else {
          errorCount++;
        }
      } catch (error) {
        console.error(`File import error (${files[i].name}):`, error);
        errorCount++;
      }
    }
    
    // Show result snackbar
    let message = `Import completed: ${successCount} successful, ${errorCount} failed`;
    if (successCount > 0) {
      message += ' (Existing data duplicates were protected)';
    }
    
    setSnackbar({
      open: true,
      message: message,
      severity: errorCount === 0 ? 'success' : (successCount > 0 ? 'warning' : 'error')
    });
    
    // Close dialog
    setImportDialogOpen(false);
    
    // Reset file input
    event.target.value = null;
    
    // Update data if at least one import was successful
    if (successCount > 0) {
      refreshData();
    }
  };

  // Export data
  const handleExportData = () => {
    const result = exportDataToFile();
    
    // Show export result snackbar
    setSnackbar({
      open: true,
      message: result ? 'Data export successful' : 'Data export failed',
      severity: result ? 'success' : 'error'
    });
    
    // Close dialog
    setExportDialogOpen(false);
  };

  // Close snackbar
  const handleSnackbarClose = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  // Open settings menu
  const handleSettingsClick = (event) => {
    setSettingsAnchorEl(event.currentTarget);
  };

  // Close settings menu
  const handleSettingsClose = () => {
    setSettingsAnchorEl(null);
  };

  // Open data deletion dialog
  const handleDeleteDataClick = () => {
    handleSettingsClose();
    setDeleteDialogOpen(true);
  };

  // Close data deletion dialog
  const handleDeleteDialogClose = () => {
    setDeleteDialogOpen(false);
  };

  // Delete all data
  const handleDeleteAllData = () => {
    const result = clearAllData();
    
    setDeleteDialogOpen(false);
    
    setSnackbar({
      open: true,
      message: result ? 'All data has been deleted' : 'Data deletion failed',
      severity: result ? 'success' : 'error'
    });
  };

  // Go to landing page
  const handleGoToLanding = () => {
    navigate('/');
  };

  // 日本語版に切り替え
  const handleSwitchToJapanese = () => {
    if (onLanguageChange) {
      onLanguageChange('ja');
    }
  };

  return (
    <AppBar position="static">
      <Toolbar>
        <Typography variant="h6" component="div" sx={{ flexGrow: 1 }}>
          SynaptiQore
        </Typography>
        
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <Button
            color="inherit"
            startIcon={<HomeIcon />}
            onClick={handleGoToLanding}
            sx={{ mr: 2 }}
          >
            Home
          </Button>
          
          <Button
            color="inherit"
            startIcon={<SettingsIcon />}
            onClick={handleSettingsClick}
            sx={{ ml: 2 }}
          >
            Settings
          </Button>
          
          <Button
            color="inherit"
            startIcon={<CloudUploadIcon />}
            onClick={handleDataMenuClick}
            sx={{ ml: 2 }}
          >
            Data Management
          </Button>

          {/* 言語切り替えボタン */}
          <Button
            color="inherit"
            onClick={handleSwitchToJapanese}
            sx={{ 
              ml: 2,
              border: '1px solid rgba(255, 255, 255, 0.3)',
              '&:hover': {
                backgroundColor: 'rgba(255, 255, 255, 0.1)',
              }
            }}
          >
            日本語
          </Button>
        </Box>
      </Toolbar>

      {/* Data Management Menu */}
      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={handleDataMenuClose}
        MenuListProps={{
          'aria-labelledby': 'data-management-button',
        }}
      >
        <MenuItem onClick={handleImportClick}>
          <CloudUploadIcon sx={{ mr: 1 }} />
          Import Data
        </MenuItem>
        <MenuItem onClick={handleExportClick}>
          <CloudDownloadIcon sx={{ mr: 1 }} />
          Export Data
        </MenuItem>
      </Menu>

      {/* Settings Menu */}
      <Menu
        anchorEl={settingsAnchorEl}
        open={settingsOpen}
        onClose={handleSettingsClose}
        MenuListProps={{
          'aria-labelledby': 'settings-button',
        }}
      >
        <MenuItem onClick={handleDeleteDataClick}>
          <DeleteForeverIcon sx={{ mr: 1 }} />
          Delete All Data
        </MenuItem>
      </Menu>

      {/* Import Dialog */}
      <Dialog open={importDialogOpen} onClose={handleImportDialogClose}>
        <DialogTitle>Import Data</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Select JSON files to import conversation data. Multiple files can be selected.
            Existing data will be preserved to avoid duplicates.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleImportDialogClose}>Cancel</Button>
          <Button onClick={handleFileSelect} variant="contained">
            Select Files
          </Button>
        </DialogActions>
      </Dialog>

      {/* Export Dialog */}
      <Dialog open={exportDialogOpen} onClose={handleExportDialogClose}>
        <DialogTitle>Export Data</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Export all conversation data as a JSON file. This file can be imported later to restore your data.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleExportDialogClose}>Cancel</Button>
          <Button onClick={handleExportData} variant="contained">
            Export
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onClose={handleDeleteDialogClose}>
        <DialogTitle>Delete All Data</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete all data? This action cannot be undone.
            Consider exporting your data before deletion.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDeleteDialogClose}>Cancel</Button>
          <Button onClick={handleDeleteAllData} color="error" variant="contained">
            Delete All
          </Button>
        </DialogActions>
      </Dialog>

      {/* Hidden file input */}
      <input
        type="file"
        ref={fileInputRef}
        onChange={handleFileChange}
        accept=".json"
        multiple
        style={{ display: 'none' }}
      />

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
      >
        <Alert onClose={handleSnackbarClose} severity={snackbar.severity} sx={{ width: '100%' }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </AppBar>
  );
};

export default EnHeader; 